import React, { useState, useEffect } from 'react';
import * as _ from 'lodash';
import { TextField, List, ListItem, Button, Box, Flex, Icon, IconButton, Tooltip, Text } from '@vibe/core';
import { AppModal, AppModalContent, AppModalFooter } from '@gorilla/ui/src/components/AppModal';
import { Template, TemplateConfig, Message } from '@gorilla/spreadsheet-shared/src/lib/spreadsheet-manager/types';
import { useSpreadsheetLoaderByTemplate } from '../hooks/use-spreadsheet-loader';
import { MessageBodyEditor } from './MessageBodyEditor';
import { createRandomId } from '../lib/utils';
import { Warning as WarningIcon, Delete as DeleteIcon, Email } from '@vibe/icons';

const INITIAL_MESSAGE_BODY_VALUE = [
  {
    type: 'paragraph',
    children: [
      { text: 'Type a message here and insert formulas ' },
      {
        type: 'formula',
        children: [{ text: '=SUM(A1:A100)' }],
      },
      { text: ' inline, that reference right to columns from your spreadsheet template.' },
    ],
  },
];

type MessagesModalProps = {
  show: boolean;
  onClose: (newConfig?: TemplateConfig) => void;
  template: Template;
};

export function MessagesModal({ show, onClose, template }: MessagesModalProps) {
  const [loadItems, setLoadItems] = useState(false);
  const { data, progress, loadingMessage } = useSpreadsheetLoaderByTemplate(loadItems ? template : undefined, 'sheetjs', false);
  //const data = null;
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
  const [messages, setMessages] = useState(_.cloneDeep(template.config.messages || []));
  const normalizedSelectedMessageId =
    selectedMessageId && messages.find((messageTemplate) => messageTemplate.id === selectedMessageId)
      ? selectedMessageId
      : messages[0]?.id || null;
  const selectedMessage = normalizedSelectedMessageId ? messages.find((message) => message.id === normalizedSelectedMessageId) : null;
  const hasMaxMessages = messages.length > 7;

  function updateMessageTemplate(newMessage: Message) {
    setMessages(
      messages.map((message) => {
        if (message.id === newMessage.id) {
          return newMessage;
        }

        return message;
      }),
    );
  }

  let modalContent: React.ReactNode | null = null;

  if (data || progress === null) {
    modalContent = (
      <>
        {selectedMessage ? (
          <Flex gap={32} align="stretch">
            <div style={{ width: '200px' }}>
              <div style={{ marginBottom: '16px' }}>
                <Button
                  style={{ width: '100%' }}
                  disabled={hasMaxMessages}
                  onClick={() => {
                    const messageId = createRandomId();

                    setMessages(
                      messages.concat({
                        id: messageId,
                        title: 'New message',
                        subject: 'Subject',
                        body: INITIAL_MESSAGE_BODY_VALUE,
                      }),
                    );

                    setSelectedMessageId(messageId);
                  }}
                >
                  Create message
                </Button>
              </div>
              <List>
                {messages.map((message) => {
                  return (
                    <ListItem
                      key={message.id}
                      selected={message.id === normalizedSelectedMessageId}
                      size="xs"
                      onClick={() => {
                        setSelectedMessageId(message.id);
                      }}
                    >
                      <div
                        style={{
                          overflow: 'hidden',
                          width: '140px',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {message.title}
                      </div>
                      <div
                        style={{
                          marginLeft: 'auto',
                        }}
                      >
                        <IconButton
                          icon={DeleteIcon}
                          kind="tertiary"
                          size="xs"
                          onClick={(ev) => {
                            ev.stopPropagation();
                            setMessages(messages.filter((otherMessage) => otherMessage.id !== message.id));
                          }}
                        />
                      </div>
                    </ListItem>
                  );
                })}
              </List>
            </div>
            <div style={{ flex: 1 }}>
              <Box border borderColor="uiBorderColor" rounded="small" shadow="xs" className="messagebox">
                <div style={{ padding: '24px 24px 24px 24px' }}>
                  <div style={{ display: 'flex', gap: '24px' }}>
                    <div style={{ width: '50%' }}>
                      <TextField
                        placeholder="Subject"
                        title="Subject"
                        value={selectedMessage?.subject}
                        debounceRate={100}
                        onBlur={(ev) => {
                          const newSubject = (ev.target as any).value;

                          updateMessageTemplate({
                            ...selectedMessage,
                            subject: newSubject || '',
                          });
                        }}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <TextField
                        placeholder="New message"
                        title="Message name"
                        value={selectedMessage?.title}
                        debounceRate={100}
                        onBlur={(ev) => {
                          updateMessageTemplate({
                            ...selectedMessage,
                            title: (ev.target as any).value || 'New message',
                          });
                        }}
                      />
                    </div>
                  </div>
                  <div style={{ position: 'relative', marginTop: '16px' }}>
                    <MessageBodyEditor
                      key={selectedMessage.id}
                      workbook={data}
                      value={selectedMessage.body}
                      onChange={(newBody) => {
                        updateMessageTemplate({
                          ...selectedMessage,
                          body: newBody,
                        });
                      }}
                    />
                    {progress === null ? (
                      <div style={{ position: 'absolute', right: '16px', bottom: '16px' }}>
                        <Tooltip content="Load board data to preview formula results" position="right" zIndex={20000}>
                          <Button
                            size="small"
                            kind="primary"
                            onClick={() => {
                              setLoadItems(true);
                            }}
                          >
                            Load results
                          </Button>
                        </Tooltip>
                      </div>
                    ) : null}
                  </div>
                </div>
              </Box>
            </div>
            <div style={{ width: '260px' }}>
              <div className="messageboxInfo">
                <Flex gap={6}>
                  <Icon icon={WarningIcon} />
                  <strong>Info</strong>
                </Flex>
                <div style={{ marginTop: '10px' }}>
                  When using this message in an email sending automation, the sender will be <strong>automation@getgorilla.app</strong>.
                  Make sure to set it as trusted email address across your organization to avoid these emails being flagged as spam.
                </div>
                <div style={{ marginTop: '10px' }}>
                  When using this message in a notification automation, the sender will be the person that configured and set up the
                  automation.
                </div>
                <div style={{ marginTop: '35px' }}>
                  <strong>
                    Learn more about troubleshooting automations and about technical limitations{' '}
                    <a
                      href="https://getgorilla.app/products/spreadsheet-gorilla/automations"
                      target="_blank"
                      style={{ textDecoration: 'underline', fontWeight: 'bold', color: '#323338' }}
                    >
                      here
                    </a>
                    .
                  </strong>
                </div>
              </div>
            </div>
          </Flex>
        ) : (
          <Flex gap={24} align="center" justify="center" direction="column" style={{ height: 'inherit' }}>
            <h2 style={{ color: '#0073ea' }}>You haven't created any messages yet.</h2>
            <p style={{ margin: '-15px 0 5px 0', textAlign: 'center' }}>
              Messages are used in your automations and can contain <br />
              formulas, that reference to this spreadsheet template.
            </p>
            <Button
              onClick={() => {
                const messageId = createRandomId();

                setMessages(
                  messages.concat({
                    id: messageId,
                    title: 'New message',
                    subject: 'Subject',
                    body: INITIAL_MESSAGE_BODY_VALUE,
                  }),
                );

                setSelectedMessageId(messageId);
              }}
            >
              Create message
            </Button>
          </Flex>
        )}
      </>
    );
  } else {
    modalContent = (
      <Flex align="center" justify="center" style={{ height: 'inherit' }}>
        {loadingMessage}
      </Flex>
    );
  }

  return (
    <AppModal
      show={show}
      onClose={() => {
        onClose();
      }}
      width="full-width"
      alertDialog={true}
      data-testid="message-modal"
    >
      <AppModalContent>
        <Flex direction="row" align="center" gap={4} style={{ marginBottom: '24px' }}>
          <Flex gap={8}>
            <Icon icon={Email} size={20} />
            <div style={{ maxWidth: '200px' }}>
              <Text type="text1" weight="medium" ellipsis>
                Message manager
              </Text>
            </div>
          </Flex>
        </Flex>
        <div
          style={{
            marginTop: '24px',
            width: '100%',
            height: '390px',
          }}
        >
          {modalContent}
        </div>
      </AppModalContent>
      <AppModalFooter>
        <Flex gap={12} justify="end">
          <Box marginEnd="auto">
            <div style={{ color: '#afb0bd' }}>
              Define messages here that will be sent to recipients in automations. You can have up to 8 messages at the moment.
            </div>
          </Box>
          <Button
            onClick={() => {
              onClose({ ...template.config, messages });
            }}
          >
            Save
          </Button>
        </Flex>
      </AppModalFooter>
    </AppModal>
  );
}
